import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, NavigationExtras, Params, Router, UrlTree } from '@angular/router';
import { Sidebar } from 'primeng/sidebar';
import { BehaviorSubject, Observable, of, Subject, Subscription, switchMap, take, takeUntil, tap } from 'rxjs';
import { CrimeGunService } from 'src/app/services/crime-gun.service';
import { LookupService } from 'src/app/services/lookup.service';
import { SearchResultType } from 'src/app/shared/app.enums';
import { AppUtils } from 'src/app/shared/app.utils';
import { AuthorizationModel } from 'src/app/shared/models/authorization-model';
import { NESSValidators } from 'src/app/shared/validators/ness.validators';
import { AuthService } from '../auth/auth.service';
import { AuthUserType } from '../auth/auth.types';
import { EventService } from '../services/event.service';
import { PreviousRouteService } from '../services/previous-route.service';
import { SearchService } from '../services/search.service';
import { AppConstants } from '../shared/app.constants';
import { AppPermissions } from '../shared/app.permissions';
import { SearchResultsComponent } from './search-results/search-results.component';

@Component({
  selector: 'ness-search',
  templateUrl: './search.component.html'
})
export class SearchComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(SearchResultsComponent, { static: false }) searchTable: SearchResultsComponent;
  private _crimeTypeName: string;
  private _userType: AuthUserType = this.authService.userType;
  private _searchRowUnselectSubj$ = new Subscription();
  event$: Observable<ServerModels.ENESS_EventDTO>;
  searchResults: ServerModels.SearchResultsViewModel[] = [];
  loadingEndRow: number = AppConstants.dbPagingDefaultRows;
  loadingStartRow: number = 1;
  loading: boolean;
  messageTemplate: string;
  searching: boolean = false;
  showSidebar: boolean;
  showEventDetail: boolean = false;
  showNIBINCaseData: boolean = false;
  hasEditPermission: boolean = false;
  readonly storageKey: string = "searchFilterModel";
  labelForSearchTerm: string = '';
  labelForSearchTermMinLength: string = "Where contains (minimum 4 characters)";
  labelForSearchTermErrorMessageMinLength: string = "Search term must be at least 4 characters.";
  labelForSearchTermFTSID: string = "Full FTS ID entry is required";
  labelForSearchTermErrorMessageFTSID: string = "FTS ID must start with prefixes: M, F, I, T and followed by 11 numbers.";
  eventStream$: BehaviorSubject<any> = new BehaviorSubject(undefined);
  crimeGunCustomFields$: Observable<ServerModels.GunDetailCustomFields>;
  showFirearmData: boolean = false;
  hasVirtualRelationToFirearm: boolean = false;
  fieldDivisionId: string = null;
  crimeGunFirearm$: Observable<ServerModels.CrimeGunFirearm>;
  errorMessage: string = 'An error occurred.';
  displayErrorStatus: boolean = false;
  currentUserType: string = AppUtils.getCurrentUserType();
  selectedResultRowIndex: number = -1;
  returnRouteNavigationExtras: NavigationExtras = null;
  currentReq: Subscription = null;
  readonly isCDSGuest = this.authService.isCDSGuest();

  readonly canEditCrimeGunFirearm: AuthorizationModel = AppPermissions.canEditCrimeGunFirearm;
  readonly canViewTraceData = AppPermissions.canViewTraceData;

  searchForm: FormGroup = this.fb.group({
    SearchField: ['', Validators.required],
    SearchTerm: ['', [Validators.required, Validators.minLength(4)]],
    PageNumber: 0,
    RowsPerPage: AppConstants.dbPagingDefaultRows,
    IncludeCDS: true
  });

  private _searchModel: ServerModels.SearchParamsViewModel = JSON.parse(sessionStorage.getItem(this.storageKey));
  get searchModel(): ServerModels.SearchParamsViewModel {
    if (this._searchModel) {
      this._searchModel.PageNumber = this._searchModel.PageNumber || 0;
      this._searchModel.RowsPerPage = this._searchModel.RowsPerPage || AppConstants.dbPagingDefaultRows;
    }
    return this._searchModel;
  }

  set searchModel(sm: ServerModels.SearchParamsViewModel) {
    this._searchModel = sm;
    sessionStorage.setItem(this.storageKey, JSON.stringify(sm));
  }
  get crimeTypeName(): string {
    return this._crimeTypeName;
  }

  set crimeTypeName(type: string) {
    this._crimeTypeName = type;
  }

  public get cdsVirtualGroups(): ServerModels.VirtualGroupViewModel[] {
    return this.authService.cdsVirtualGroups;
  }

  private _destroyed$: Subject<void> = new Subject();

  constructor(private searchService: SearchService,
    public crimeGunService: CrimeGunService,
    public lookupService: LookupService,
    private eventService: EventService,
    private authService: AuthService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private previousRouteService: PreviousRouteService
  ) { }

  ngOnInit() {
    this.labelForSearchTerm = this.labelForSearchTermErrorMessageMinLength;
    this.route.queryParams.pipe(takeUntil(this._destroyed$)).subscribe((params: Params) => {
      if (params && Object.keys(params).length > 0) {
        const type: SearchResultType = <SearchResultType>params['type'];
        const id: string = params['dataId'];
        const rowIndex = parseInt(params['rowIndex']) || -1;
        this.selectedResultRowIndex = rowIndex;
        if (type && id && rowIndex) {
          this.onShowDetails({ detailType: type, dataId: id, rowIndex: rowIndex });
        }
        this._clearRouteParams();
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this._searchRowUnselectSubj$ = this.searchTable.dataTable.onRowUnselect.subscribe((evt) => this._clearRouteParams());
      if (this.searchModel && Object.keys(this.searchModel).length > 0) {
        this.searchForm.setValue({
          SearchField: this.searchModel.SearchField || '',
          SearchTerm: this.searchModel.SearchTerm || '',
          PageNumber: 0,
          RowsPerPage: AppConstants.dbPagingDefaultRows,
          IncludeCDS: true
        });

        this.messageTemplate = 'Loading...';
        this.searchTable.dataTable.reset();
        this.searchResults = [];
        this.searchModel = this.searchForm.value as ServerModels.SearchParamsViewModel;
        this._getSearchResults();
      }
    });
  }

  ngOnDestroy() {
    this._destroyed$.next();
    this._destroyed$.complete();
    this._searchRowUnselectSubj$.unsubscribe();
  }

  public onSelectedResult(evt: { result: ServerModels.SearchResultsViewModel, resultType: SearchResultType, rowIndex: number }): void {
    switch (evt.resultType) {
      case SearchResultType.RMSEvent:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'type': SearchResultType.RMSEvent,
            'dataId': evt.result.EventID,
            'rowIndex': evt.rowIndex
          }
        } as NavigationExtras;
        this._viewEventDetails(evt.result);
        break;
      case SearchResultType.FirearmData:
        this._viewFirearmDetails(evt.result);
        break;
      case SearchResultType.NIBINCaseData:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': evt.rowIndex
          }
        };
        this._viewNIBINCaseData(evt.result);
        break;
      case SearchResultType.CrimeGunDetail:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': evt.rowIndex
          }
        };
        let returnUrlTree: UrlTree = this.router.createUrlTree(['/search'], this.returnRouteNavigationExtras);
        this.previousRouteService.returnUrl = this.router.serializeUrl(returnUrlTree);
        break;
    };
  }

  public onShowDetails(evt: { detailType: SearchResultType, dataId: string, rowIndex?: number }): void {
    this.clearSidebar();
    switch (evt.detailType) {
      case SearchResultType.RMSEvent:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'type': SearchResultType.RMSEvent,
            'dataId': <string>evt.dataId,
            'rowIndex': evt.rowIndex || parseInt(this.returnRouteNavigationExtras?.queryParams['rowIndex'])
          }
        } as NavigationExtras;
        this._getEventDetails(<string>evt.dataId);
        break;
      case SearchResultType.FirearmData:
          let firearmId = evt.dataId;
          if (!!firearmId) {
            this.showCrimeGunCustomFields(firearmId);
          }
        break;
      case SearchResultType.CrimeGunDetail:
        this.returnRouteNavigationExtras = {
          queryParams: {
            'rowIndex': (evt.rowIndex || parseInt(this.returnRouteNavigationExtras?.queryParams['rowIndex']))
          }
        };
        let returnUrlTree: UrlTree = this.router.createUrlTree(['/search'], this.returnRouteNavigationExtras);
        this.previousRouteService.returnUrl = this.router.serializeUrl(returnUrlTree);
        break;
    }
  }

  private _getEventDetails(eventId: string): void {
    this.event$ = this.eventService.getEventDataModelById(eventId).pipe(tap((x: ServerModels.ENESS_EventDTO) => {
      this.showSidebar = this.showEventDetail = true;
    }));
  }

  private _viewNIBINCaseData(result: ServerModels.SearchResultsViewModel): void {
    if (result.NIBINCaseID) {
      this.crimeGunService.getNIBINCaseData(result.NIBINCaseID);
      this.showSidebar = this.showNIBINCaseData = true;
    }
  }

  private _viewEventDetails(result: ServerModels.SearchResultsViewModel): void {
    if (result.EventID) {
      this.crimeTypeName = result.CrimeTypeDesc || '';
      this._getEventDetails(result.EventID);
    }
  }


  private _viewFirearmDetails(result: ServerModels.SearchResultsViewModel): void {
    let firearmId = result.FirearmID;
    this.showCrimeGunCustomFields(firearmId);
  }

  private _getSearchResults(): void {
    this.loading = true;
    this.searching = true;
    this.currentReq = this.searchService.getSearchResults(this.searchModel).pipe(take(1)).subscribe({
      next: (results: ServerModels.SearchResultsViewModel[]) => {
        if (results == null || typeof (results) === 'undefined') {
          this.loading = false;
          this.searching = false;
        }

        this.loading = results.length > 0;
        this.searching = false;
        if (results.length > 0) {
          this.loadingStartRow = this.searchModel.PageNumber == 0 ? 1 : (1 + this.searchResults.length);
          this.searchResults = this.searchResults.concat(results);
          this.loadingEndRow = this.searchResults.length;
          this.messageTemplate = `Loaded rows ${this.loadingStartRow} thru ${this.loadingEndRow}...`;
          if (this.loading = results.length == AppConstants.dbPagingDefaultRows) {
            this.searchModel.PageNumber += 1;
            this._getSearchResults();
          }
        }
      },
      error: (err:any) => {
        this.loading = false;
        this.searching = false;
      },
      complete: () => this.searchTable.selectedResultRow = !!this.selectedResultRowIndex ? this.searchResults[this.selectedResultRowIndex] : null
    });
  }

  private _clearRouteParams(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: null
    });
  }

  public onClear(): void {
    if (this.currentReq) this.currentReq.unsubscribe();
    sessionStorage.removeItem(this.storageKey);
    this.searchModel = null;
    this.searchForm.reset({ SearchField: '' });
    this.labelForSearchTerm = this.labelForSearchTermMinLength;
    this.showSidebar = false;
    this.loading = false;
    this.searching = false;
    this.searchResults = [];
    this.loadingStartRow = 1;
    this.loadingEndRow = this.searchResults.length;
    this.selectedResultRowIndex = -1;
    this.searchTable.selectedResultRow = null;
    this.searchTable?.dataTable?.reset();
    this._clearRouteParams();
  }

  public onSearch(): void {
    if (this.searchForm.valid) {
      this.messageTemplate = 'Loading...';
      this.searchResults = [];
      this.selectedResultRowIndex = -1;
      this.searchTable.dataTable.reset();
      this.searchModel = this.searchForm.value as ServerModels.SearchParamsViewModel;
      this.searchModel.PageNumber = 0;
      this.searchModel.RowsPerPage = AppConstants.dbPagingDefaultRows;
      this.loadingStartRow = 1;
      this.loadingEndRow = this.searchModel.RowsPerPage;
      this._getSearchResults();
    } else {
      this.onClear();
    }
  }

  public onShowSidebar(sideBar: Sidebar): void {
    window.setTimeout(() => {
      (<HTMLElement>sideBar.el.nativeElement).setAttribute("tabindex", "-1");
      (<HTMLElement>sideBar.el.nativeElement).focus();
    }, 1000);
  }

  public onSelectSearchField(searchField: string): void {
    if (searchField == "FTSID") {
      this.searchForm.get('SearchTerm').setValidators([Validators.required, NESSValidators.FTSIDValidator]);
      this.labelForSearchTerm = this.labelForSearchTermFTSID;
    } else {
      this.searchForm.get('SearchTerm').setValidators([Validators.required, Validators.minLength(4)]);
      this.labelForSearchTerm = this.labelForSearchTermMinLength;
    }

    this.searchForm.get('SearchTerm').updateValueAndValidity();
  }

  public showCrimeGunCustomFields(firearmId: string) {
    this.eventStream$.next(undefined);
    this.crimeGunCustomFields$ = this.eventStream$.pipe(
      take(1),
      switchMap(() => this.crimeGunService.getCrimeGunCustomFields(firearmId).pipe(tap((data: ServerModels.GunDetailCustomFields) => {
        this.showSidebar = true;
        this.showFirearmData = true;
        this.hasVirtualRelationToFirearm = !this.authService.hasUserType(['StateLocalLE']) || this.authService.canEdit(data.VirtualGroupRelation)
        return of(data);
      }))));
  }

  public closeFirearmView(): void {
    this.clearSidebar()
  }

  public onFirearmUpdate(data: ServerModels.GunDetailCustomFields): void {
    if (data == null) {
      return;
    }
    this.crimeGunService.addOrUpdateCustomCrimeGunFields(data).subscribe((customFields: ServerModels.GunDetailCustomFields) => {
      this.crimeGunFirearm$ = this.crimeGunService.getCrimeGunFirearm(data.CrimeGunNumberID).pipe(
        switchMap((firearm: ServerModels.CrimeGunFirearm) => {
          if (!!customFields.FirearmTraceData.length) {
            firearm.FTSIDs = customFields.FirearmTraceData.map(x => x.FTSID)?.join("; ");
            firearm.FirearmType = customFields.FirearmTraceData[0].FTSType;
            firearm.PurchaseCity = customFields.FirearmTraceData[0].PurchaseCity;
            firearm.PurchaseDate = customFields.FirearmTraceData[0].PurchaseDate;
            firearm.PurchaseState = customFields.FirearmTraceData[0].PurchaseState;
            firearm.FTSTypeID = customFields.FirearmTraceData[0].FTSTypeID;
          }

          return of(firearm);
        }));
    },
      (err:any) => {
        this.errorMessage = err;
        this.displayErrorStatus = true;
      },
      () => this.clearSidebar()
    );
  }

  public clearSidebar(): void {
    this.showEventDetail = false;
    this.showNIBINCaseData = false;
    this.showFirearmData = false;
    this.showSidebar = false;
    this._clearRouteParams();
  }
}
